import rootStore from '@vue-storefront/core/store';
import VueRouter, { RouteConfig } from 'vue-router'
import { RouterManager } from '@vue-storefront/core/lib/router-manager'
import { ErrorHandler, RawLocation, Route } from 'vue-router/types/router'
import { once } from './once'

once('__VUE_EXTEND_PUSH_RR__', () => {
  const originalPush = VueRouter.prototype.push
  ;(VueRouter.prototype as any).push = function push (location: RawLocation, onComplete = () => {}, onAbort?: ErrorHandler): Promise<Route> {
    if (onComplete || onAbort) return originalPush.call(this, location, onComplete, onAbort)
    if (process.env.NODE_ENV === 'development') {
      // In development we want router errors to be visible.
      return originalPush.call(this, location)
    }
    return originalPush.call(this, location).catch(err => err)
  }
})

export const createRouter = (): VueRouter => {
  // Do not use `__dirname` here: webpack's SSR bundle can leave it undefined, which breaks
  // Node's URL helpers (pathToFileURL) when vue-router resolves the base.
  const base =
    (typeof process !== 'undefined' && process.env && process.env.VSF_ROUTER_BASE) ||
    '/'

  return new VueRouter({
    mode: 'history',
    base,
    scrollBehavior: (to, from) => {
      if (to.hash) {
        return {
          selector: to.hash
        }
      }
      if (rootStore.getters['url/isBackRoute']) {
        const { scrollPosition = { x: 0, y: 0 } } = rootStore.getters['url/getCurrentRoute']
        return scrollPosition
      } else if (to.path !== from.path) { // do not change scroll position when navigating on the same page (ex. change filters)
        return { x: 0, y: 0 }
      }
    }
  })
}

export const createRouterProxy = (router: VueRouter): VueRouter => {
  return new Proxy(router, {
    get (target, propKey) {
      const origMethod = target[propKey]

      if (propKey === 'addRoutes') {
        return function (routes: RouteConfig[], ...args): void {
          return RouterManager.addRoutes(routes, ...args)
        }
      }

      if (typeof origMethod === 'function') {
        // Keep method context on the real VueRouter instance so hooks
        // registered through the proxy are visible to baseRouter in SSR.
        return origMethod.bind(target)
      }
      return origMethod
    }
  })
}
